import { useState, useCallback, useEffect } from 'react';
import { useAuth } from 'context/auth/Auth';
import eventsApi from 'api/requests/events';
import { useShowLoginModal } from 'context/general/ShowLoginModal';
import { useFavoritedEventsOptional } from 'context/events/FavoritedEvents';

const STATUS_FAVORITE = 4;
const STATUS_INTEREST = 2;

// Module-level store so all instances sharing the same slug stay in sync
const _store = new Map();        // slug → boolean
const _listeners = new Map();    // slug → Set<setState>
const _toggledSlugs = new Set(); // slugs manually toggled this session (user choice wins over API)

function _notify(slug, value) {
    _store.set(slug, value);
    _listeners.get(slug)?.forEach(fn => fn(value));
}

function _subscribe(slug, setState) {
    if (!_listeners.has(slug)) _listeners.set(slug, new Set());
    _listeners.get(slug).add(setState);
    return () => _listeners.get(slug)?.delete(setState);
}

export default function useFavorite(eventSlug, initialFavorited = false, eventData = null) {
    const { isAuth } = useAuth();
    const { setShowLoginModal } = useShowLoginModal();
    const favCtx = useFavoritedEventsOptional();

    // If the user hasn't manually toggled this slug, always trust the API value.
    // This ensures the button reflects the real favorite state on every render.
    if (!_toggledSlugs.has(eventSlug)) {
        _store.set(eventSlug, initialFavorited);
    }

    const [isFavorited, setIsFavorited] = useState(() => _store.get(eventSlug));
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        // Sync local state with store (covers slug changes and post-toggle renders)
        setIsFavorited(_store.get(eventSlug));
        return _subscribe(eventSlug, setIsFavorited);
    }, [eventSlug]);

    const toggleFavorite = useCallback(async () => {
        if (!isAuth) {
            setShowLoginModal(true);
            return;
        }

        if (loading) return;

        const newValue = !_store.get(eventSlug);
        const previousValue = _store.get(eventSlug);
        const newStatus = newValue ? STATUS_FAVORITE : STATUS_INTEREST;

        _toggledSlugs.add(eventSlug); // user choice now wins over API initialFavorited
        _notify(eventSlug, newValue);

        // Optimistic context update
        if (newValue && eventData) favCtx?.addFavorite(eventData);
        else if (!newValue) favCtx?.removeFavorite(eventSlug);

        setLoading(true);

        try {
            await eventsApi.interest(eventSlug, newStatus);
        } catch (error) {
            _notify(eventSlug, previousValue);
            // Rollback context update
            if (newValue) favCtx?.removeFavorite(eventSlug);
            else if (eventData) favCtx?.addFavorite(eventData);
        } finally {
            setLoading(false);
        }
    }, [isAuth, loading, eventSlug, setShowLoginModal, eventData, favCtx]);

    return { isFavorited, toggleFavorite, loading };
}
